import React, {
    Fragment,
    useCallback,
    useState,
    useEffect,
    useMemo,
} from 'react';
import {
    useNavigate,
} from 'react-router-dom';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import classes from 'classnames';
import { useStores } from '../../hooks/use-stores';
import SubjectsIdRouteElement from './SubjectsIdRouteElement';
import SubjectsDefaultRedirectElement from './SubjectsDefaultRedirectElement';
import {
    FlexDiv,
    Heading4,
    TextDefault,
    TextLabel,
} from '@awarego-ui/components';
import ViewHeader from '../../components/ViewHeader';
import MenuIcon from '@material-ui/icons/Menu';
import MenuOpenIcon from '@material-ui/icons/MenuOpen';
import LanguageSelector from './language-selector';

export default observer(
    ({
        parentUrl,
        training,
        subjectId,
        trainingStore,
        store,
        videoTokenStore,
        onClose,
        isDemo,
        isScorm,
        availableLanguages,
        selectedLanguage,
        onSelectLanguage,
    }) => {
        const { t } = useTranslation();
        let navigate = useNavigate();

        const [isFinished, setIsFinished] = useState(false);
        const [showMobileMenu, setShowMobileMenu] = useState(false);

        const {
            brandingStore,
        } = useStores();

        const { brandingLoaded, account_name, logo, trainingSkipAutoFinish } =
            brandingStore;

        const subject = trainingStore.training.subjects.find(
            (x) => x.id === subjectId
        );

        const showLessonMenu = () => {
            setShowMobileMenu(true);
        };
        const hideLessonMenu = () => {
            setShowMobileMenu(false);
        };

        const onNextLesson = async () => {
            await trainingStore.reloadTraining();
        };

        const handleClosePage = useCallback(() => {
            if (onClose) {
                return onClose();
            } else {
                navigate(parentUrl);
            }
        }, [history, onClose, parentUrl]);

        const onFinish = async () => {
            await trainingStore.reloadTraining();
            let lastNextEnabled = trainingStore.training.nextSubjectDate
                ? true
                : trainingStore.certificateQualified;

            console.log('onFinish: lastNextEnabled', lastNextEnabled);
            if (lastNextEnabled) setIsFinished(true);
        };

        const {
            certificateQualified,
            assessmentAvailable,
            availableUnfinishedSubjects,
            subjects,
        } = trainingStore;

        useEffect(() => {
            if (isScorm && certificateQualified) {
                if (!trainingSkipAutoFinish) setIsFinished(true);
            }
        }, [isScorm, certificateQualified]);

        let lastNextEnabled = training.nextSubjectDate
            ? true
            : certificateQualified;

        let score =
            training.subjectsSent > 0
                ? (100 * (training.viewed || 0)) / training.subjectsSent
                : 0;

        const trainingName = useMemo(() => {
            if (!training) return;
            if (isDemo) return 'Cybersecurity Awareness Training';
            return training.name;
        }, [training, isDemo]);

        const videosWatchedText = useMemo(() => {
            return (
                <Fragment>
                    {trainingStore.viewedVideos || training.viewed}/
                    {training.subjectsSent} {t('videos watched')}
                </Fragment>
            );
        }, [
            t,
            training.subjectsSent,
            training.viewed,
            trainingStore.viewedVideos,
        ]);

        return (
            <div
                className={classes('wizard', {
                    scorm: subject && subject.type === 2,
                })}
            >
                <ViewHeader
                    onClose={handleClosePage}
                    isDemo={isDemo}
                    brandingLoaded={brandingLoaded}
                    logo={logo}
                    account_name={account_name}
                    done={score}
                    mobileMainHeading={<Heading4>{trainingName}</Heading4>}
                    mobileSubheading={
                        <TextLabel bold lighter>
                            {videosWatchedText}
                        </TextLabel>
                    }
                    mobileRightCorner={
                        <FlexDiv marginLeftAuto>
                            <FlexDiv $noWrap alignCenter gap={16}>
                                {availableLanguages && !brandingStore.hideLanguageList ? (
                                    <LanguageSelector
                                        selectedLanguage={selectedLanguage}
                                        handleSelectLanguage={onSelectLanguage}
                                        availableLanguages={availableLanguages}
                                    />
                                ) : null}
                                <FlexDiv>
                                    {!showMobileMenu ? (
                                        <MenuIcon onClick={showLessonMenu} />
                                    ) : (
                                        <MenuOpenIcon
                                            onClick={hideLessonMenu}
                                        />
                                    )}
                                </FlexDiv>
                            </FlexDiv>
                        </FlexDiv>
                    }
                    desktopMainHeading={trainingName}
                    desktopRightCorner={
                        <FlexDiv $noWrap alignCenter gap={16}>
                            {availableLanguages && !brandingStore.hideLanguageList ? (
                                <LanguageSelector
                                    selectedLanguage={selectedLanguage}
                                    handleSelectLanguage={onSelectLanguage}
                                    availableLanguages={availableLanguages}
                                />
                            ) : null}
                            <TextDefault bold lighter noWrap>
                                {videosWatchedText}
                            </TextDefault>
                        </FlexDiv>
                    }
                />
                {subjectId ? (
                    <SubjectsIdRouteElement
                        isFinished={isFinished}
                        training={training}
                        isDemo={isDemo}
                        store={store}
                        isScorm={isScorm}
                        showMobileMenu={showMobileMenu}
                        hideLessonMenu={hideLessonMenu}
                        certificateQualified={certificateQualified}
                        assessmentAvailable={assessmentAvailable}
                        parentUrl={parentUrl}
                        subjectId={subjectId}
                        subject={subject}
                        videoTokenStore={videoTokenStore}
                        availableUnfinishedSubjects={availableUnfinishedSubjects}
                        lastNextEnabled={lastNextEnabled}
                        onFinish={onFinish}
                        handleClosePage={handleClosePage}
                    />
                ) : (
                    <SubjectsDefaultRedirectElement
                        training={training}
                        parentUrl={parentUrl}
                        availableUnfinishedSubjects={availableUnfinishedSubjects}
                        subjects={subjects}
                    />
                )}
            </div>
        );
    }
);
